import { useMemo } from 'react';
import { Textarea } from '@mlm/ui';
import type { Membership } from '../../../lib/api';

/**
 * Placeholder editor for per-tier plans that don't have a typed UI yet
 * (mentor_bonus, monthly_direct_bonus, unilevel_consecutive_bonus). Shows
 * the relevant slice of each tier's config and lets the admin edit it as
 * JSON inline. Saving still goes through the parent's PATCH flow.
 *
 * The slice is extracted by `pickFor(planCode)` so unrelated keys aren't
 * shown. Editing modifies just that slice, leaving the rest of the
 * config intact.
 */
export function JsonPerTierFallback({
  planCode,
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  planCode: string;
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const path = useMemo(() => sliceFor(planCode), [planCode]);

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-muted-foreground">
        Typed editor not built yet. Paste valid JSON for each tier. Invalid
        JSON is ignored on blur. The slice edited here is{' '}
        <code className="font-mono">{path.label}</code>.
      </p>
      <div className="space-y-3">
        {memberships.map((m) => {
          const slice = path.read(draft[m.id] ?? {});
          return (
            <div key={m.id} className="border rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium">
                  <span className="font-mono">{m.code}</span>{' '}
                  <span className="text-muted-foreground">· {m.name}</span>
                </p>
              </div>
              <Textarea
                className="h-32 text-[10px] font-mono"
                value={JSON.stringify(slice ?? {}, null, 2)}
                disabled={readOnly}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    onChange(m.id, path.write(draft[m.id] ?? {}, parsed));
                  } catch {
                    /* ignore — leave draft unchanged until JSON is valid */
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

type Path = {
  label: string;
  read(cfg: Record<string, unknown>): unknown;
  write(cfg: Record<string, unknown>, value: unknown): Record<string, unknown>;
};

function sliceFor(planCode: string): Path {
  switch (planCode) {
    case 'mentor_bonus':
      return {
        label: 'config.computation.mentor_bonus',
        read: (c) =>
          (c.computation as Record<string, unknown> | undefined)?.mentor_bonus ?? {},
        write: (c, v) => mergeAt(c, ['computation', 'mentor_bonus'], v),
      };
    case 'monthly_direct_bonus':
      return {
        label: 'config.computation.monthly_direct_bonus',
        read: (c) =>
          (c.computation as Record<string, unknown> | undefined)?.monthly_direct_bonus ?? {},
        write: (c, v) => mergeAt(c, ['computation', 'monthly_direct_bonus'], v),
      };
    case 'unilevel_consecutive_bonus':
      return {
        label: 'config.unilevel_consecutive_bonus',
        read: (c) => c.unilevel_consecutive_bonus ?? {},
        write: (c, v) => mergeAt(c, ['unilevel_consecutive_bonus'], v),
      };
    default:
      return {
        label: `config.${planCode}`,
        read: (c) => c[planCode] ?? {},
        write: (c, v) => mergeAt(c, [planCode], v),
      };
  }
}

/** Immutable deep-set at the given path. Replaces the leaf with `value`. */
function mergeAt(
  obj: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  if (path.length === 0) return obj;
  const [head, ...rest] = path;
  if (rest.length === 0) {
    const next = { ...obj };
    if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0) {
      delete next[head!];
    } else {
      next[head!] = value;
    }
    return next;
  }
  const child = (obj[head!] as Record<string, unknown>) ?? {};
  const merged = mergeAt(child, rest, value);
  const next = { ...obj };
  if (Object.keys(merged).length === 0) delete next[head!];
  else next[head!] = merged;
  return next;
}
